import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Switch,
  TextField,
  Select,
  MenuItem,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Alert,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const AdvancedSettings = ({ value = {}, onChange, disabled = false }) => {
  // Default values for advanced settings matching DDL
  const defaultSettings = {
    show_daybook_details: true,
    report_order: 999,
    gst_applicable: false,
    hsn_sac_code: "",
    type_of_supply: "Services",
    interest_enabled: false,
    interest_rate: 0.0,
    interest_type: "Simple",
    interest_from: "Due Date",
    inventory_values_affected: false,
    enable_cheque_tracking: false,
    enable_bank_reconciliation: false,
    lock_for_posting: false,
  };

  // Merge provided value with defaults
  const advancedSettings = { ...defaultSettings, ...value };

  const handleToggle = (field) => {
    const newSettings = {
      ...advancedSettings,
      [field]: !advancedSettings[field],
    };

    // Clear dependent fields when parent is disabled
    if (field === "gst_applicable" && !newSettings.gst_applicable) {
      newSettings.hsn_sac_code = "";
      newSettings.type_of_supply = "";
    }

    if (field === "interest_enabled" && !newSettings.interest_enabled) {
      newSettings.interest_rate = 0;
    }

    onChange(newSettings);
  };

  const handleChange = (field) => (event) => {
    const newSettings = {
      ...advancedSettings,
      [field]: event.target.value,
    };
    onChange(newSettings);
  };

  return (
    <Grid container spacing={3}>
      {/* Ledger & Daybook */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Ledger & Daybook
            </Typography>

            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={advancedSettings.show_daybook_details}
                    onChange={() => handleToggle("show_daybook_details")}
                    disabled={disabled}
                  />
                }
                label="Show details in Daybook"
              />
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ ml: 4 }}
              >
                Displays voucher-level breakup in daybook
              </Typography>
            </Box>

            <Box sx={{ mt: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1,
                }}
              >
                <Typography variant="body2" fontWeight={500}>
                  Account Report Order
                </Typography>
                <Tooltip title="Lower number appears earlier in reports">
                  <InfoOutlinedIcon fontSize="small" color="action" />
                </Tooltip>
              </Box>
              <TextField
                type="number"
                value={advancedSettings.report_order}
                onChange={handleChange("report_order")}
                size="small"
                fullWidth
                disabled={disabled}
              />
              <Typography
                variant="caption"
                color="primary"
                display="block"
                sx={{ mt: 0.5 }}
              >
                ℹ️ Lower number appears earlier in reports
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* GST & Tax */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              GST & Tax
            </Typography>

            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={advancedSettings.gst_applicable}
                    onChange={() => handleToggle("gst_applicable")}
                    disabled={disabled}
                  />
                }
                label="GST Applicable"
              />
            </Box>

            <Box sx={{ mt: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1,
                }}
              >
                <Typography variant="body2" fontWeight={500}>
                  HSN / SAC Code
                </Typography>
                <Tooltip title="HSN or SAC Code for GST">
                  <InfoOutlinedIcon fontSize="small" color="action" />
                </Tooltip>
              </Box>
              <TextField
                value={advancedSettings.hsn_sac_code}
                onChange={handleChange("hsn_sac_code")}
                size="small"
                fullWidth
                disabled={!advancedSettings.gst_applicable || disabled}
                placeholder="Enter HSN/SAC Code"
              />
            </Box>

            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" fontWeight={500} gutterBottom>
                Type of Supply
              </Typography>
              <Select
                value={advancedSettings.type_of_supply}
                onChange={handleChange("type_of_supply")}
                size="small"
                fullWidth
                disabled={!advancedSettings.gst_applicable || disabled}
              >
                <MenuItem value="Goods">Goods</MenuItem>
                <MenuItem value="Services">Services</MenuItem>
              </Select>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Interest Calculation */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Interest Calculation
            </Typography>

            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={advancedSettings.interest_enabled}
                    onChange={() => handleToggle("interest_enabled")}
                    disabled={disabled}
                  />
                }
                label="Enable Interest Calculation"
              />
            </Box>

            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" fontWeight={500} gutterBottom>
                Interest Rate (%)
              </Typography>
              <TextField
                type="number"
                value={advancedSettings.interest_rate}
                onChange={handleChange("interest_rate")}
                size="small"
                fullWidth
                disabled={!advancedSettings.interest_enabled || disabled}
                inputProps={{ step: 0.01, min: 0, max: 100 }}
              />
            </Box>

            <Box sx={{ mt: 3 }}>
              <FormControl
                component="fieldset"
                disabled={!advancedSettings.interest_enabled || disabled}
              >
                <FormLabel component="legend">Interest Type</FormLabel>
                <RadioGroup
                  row
                  value={advancedSettings.interest_type}
                  onChange={handleChange("interest_type")}
                >
                  <FormControlLabel
                    value="Simple"
                    control={<Radio />}
                    label="Simple"
                  />
                  <FormControlLabel
                    value="Compound"
                    control={<Radio />}
                    label="Compound"
                  />
                </RadioGroup>
              </FormControl>
            </Box>

            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" fontWeight={500} gutterBottom>
                Interest From
              </Typography>
              <Select
                value={advancedSettings.interest_from}
                onChange={handleChange("interest_from")}
                size="small"
                fullWidth
                disabled={!advancedSettings.interest_enabled || disabled}
              >
                <MenuItem value="Due Date">Due Date</MenuItem>
                <MenuItem value="Bill Date">Bill Date</MenuItem>
              </Select>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Inventory Impact */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Inventory Impact
            </Typography>

            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={advancedSettings.inventory_values_affected}
                    onChange={() => handleToggle("inventory_values_affected")}
                    disabled={disabled}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <span>Inventory Values Affected</span>
                    <Tooltip title="Affects inventory valuation">
                      <InfoOutlinedIcon fontSize="small" color="action" />
                    </Tooltip>
                  </Box>
                }
              />
            </Box>

            <Alert severity="warning" sx={{ mt: 2 }}>
              Enable only for Purchase / Sales ledgers
            </Alert>
          </CardContent>
        </Card>
      </Grid>

      {/* Bank Controls */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Bank Controls
            </Typography>

            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={advancedSettings.enable_cheque_tracking}
                    onChange={() => handleToggle("enable_cheque_tracking")}
                    disabled={disabled}
                  />
                }
                label="Enable Cheque Tracking"
              />
            </Box>

            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={advancedSettings.enable_bank_reconciliation}
                    onChange={() => handleToggle("enable_bank_reconciliation")}
                    disabled={disabled}
                  />
                }
                label="Enable Bank Reconciliation"
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Controls */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Controls
            </Typography>

            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={advancedSettings.lock_for_posting}
                    onChange={() => handleToggle("lock_for_posting")}
                    disabled={disabled}
                  />
                }
                label="Lock account for posting"
              />
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ ml: 4 }}
              >
                Prevents any new entries to this account
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default AdvancedSettings;
